import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { adsApiCall } from "./api";
import { Loader2, ArrowLeft, Sparkles, Zap, CheckCircle2 } from "lucide-react";
import { toast, Toaster } from "sonner";
import AchievementCelebration from "./AchievementCelebration";
import "./ads-creator.css";

const ANGLES = [
  { id: "fomo",      label: "FOMO",       hint: "Scarcity & urgency" },
  { id: "benefit",   label: "Benefit",    hint: "Concrete value" },
  { id: "curiosity", label: "Curiosity",  hint: "Open loop" },
  { id: "authority", label: "Authority",  hint: "Social proof" },
  { id: "emotion",   label: "Emotion",    hint: "Before → after" },
];

const FORMATS = [
  { id: "facebook_feed",    label: "Facebook Feed",    size: "1200×628"  },
  { id: "instagram_feed",   label: "Instagram Feed",   size: "1080×1080" },
  { id: "instagram_story",  label: "Instagram Story",  size: "1080×1920" },
  { id: "linkedin_banner",  label: "LinkedIn Banner",  size: "1584×396"  },
  { id: "google_display",   label: "Google Display",   size: "300×250"   },
];

const EXAMPLES = [
  "Eco-friendly running shoes for weekend trail runners — 20% off launch week",
  "AI bookkeeping for freelancers — saves 8 hrs/month, €19/mo, free trial",
  "Handmade sourdough delivery in Bratislava — fresh every Saturday morning",
];

export default function InstantCampaign() {
  const [prompt, setPrompt] = useState("");
  const [language, setLanguage] = useState("en");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [remixSeed, setRemixSeed] = useState(null); // {origin_campaign_id, title, origin_prompt, ...}
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const remixKey = searchParams.get("remix");

  useEffect(() => {
    if (!remixKey) return;
    (async () => {
      try {
        const seed = await adsApiCall.remixSeed(remixKey);
        setRemixSeed(seed);
        // Scaffold the prompt with a remix-friendly template
        if (seed.origin_prompt) {
          setPrompt(`Same concept but for my business: ${seed.origin_prompt}`);
        }
        if (seed.origin_language) setLanguage(seed.origin_language);
      } catch (e) {
        if (e?.response?.status === 404) toast.error("Origin campaign not found or not public.");
      }
    })();
  }, [remixKey]);

  async function run() {
    if (prompt.trim().length < 6) {
      toast.error("Describe your offer in at least a few words.");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const payload = { prompt: prompt.trim(), language };
      if (remixSeed?.origin_campaign_id) payload.remix_of = remixSeed.origin_campaign_id;
      const res = await adsApiCall.aiInstantCampaign(payload);
      setResult(res);
      // If the backend unlocked any achievements on this run, fire the
      // celebration event immediately (before we navigate away).
      if (Array.isArray(res?.newly_unlocked) && res.newly_unlocked.length > 0) {
        window.dispatchEvent(new CustomEvent("ads:achievement-unlocked", { detail: res.newly_unlocked }));
      }
      toast.success(`${res.count} ads generated — opening in My Ads…`);
      setTimeout(() => navigate("/ads-creator/my-ads"), 1600);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 401) {
        toast.error("Please log in to run Instant Campaign.");
        navigate("/login");
      } else if (detail?.code === "credits_exhausted" || typeof detail === "string" && detail.includes("credit")) {
        toast.error("Out of ad credits — upgrade to generate 25 ads in one shot.");
        navigate("/ads-creator#pricing");
      } else {
        toast.error(detail?.message || detail || "Campaign failed. Try a different prompt.");
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="ads-root" data-testid="ads-instant-campaign">
      <Helmet>
        <title>Instant Campaign — 25 ads in one click | TopRankPulse</title>
        <meta name="description" content="One prompt, 25 ads. Generate 5 angles × 5 formats with TopRankPulse Instant Campaign." />
        <meta property="og:title" content="Instant Campaign — 25 ads in one click | TopRankPulse" />
        <meta property="og:description" content="One prompt, 25 ads. Generate 5 angles × 5 formats instantly." />
        <meta property="og:image" content="https://toprankpulse.com/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://toprankpulse.com/og-image.png" />
      </Helmet>
      <Toaster position="top-right" theme="dark" />
      <div className="ads-hero" style={{ padding: "56px 28px 32px" }}>
        <div className="ads-container">
          <Link to="/ads-creator" className="ads-btn ads-btn-ghost" style={{ marginBottom: 18 }} data-testid="ic-back">
            <ArrowLeft size={16} /> Back
          </Link>
          {remixSeed ? (
            <div className="ic-remix-banner" data-testid="ic-remix-banner">
              <Sparkles size={14} />
              <span>Remixing <b>"{remixSeed.title}"</b> — the original author gets +1 credit when you ship this.</span>
            </div>
          ) : null}
          <span className="ads-eyebrow"><Zap size={12} style={{ display: "inline", marginRight: 4 }} /> {remixSeed ? "Remix Campaign" : "Instant Campaign"}</span>
          <h1 className="ads-h1" style={{ fontSize: "clamp(36px, 5.2vw, 64px)" }}>
            One prompt. <span style={{ color: "#c084fc" }}>25 ads.</span> In a minute.
          </h1>
          <p className="ads-lead" style={{ maxWidth: 720 }}>
            Describe your offer once. We generate 5 angles × 5 formats — all animated,
            all on-brand, all ready to ship. Skip the brief, skip the agency.
          </p>
        </div>
      </div>

      <div className="ads-section" style={{ paddingTop: 18 }}>
        <div className="ads-container">
          <div className="ic-grid">
            {/* Left: input */}
            <div className="ic-input-card" data-testid="ic-input-card">
              <div className="ads-panel-title">Describe your offer</div>
              <textarea
                className="ads-prop-input"
                rows={5}
                placeholder="Example: Eco-friendly running shoes for weekend trail runners — 20% off launch week."
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                data-testid="ic-prompt"
                style={{ fontSize: 15, lineHeight: 1.5, padding: 14 }}
              />
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
                {EXAMPLES.map((ex, i) => (
                  <button key={i} className="ads-chip" onClick={() => setPrompt(ex)} data-testid={`ic-example-${i}`}>
                    Try example #{i + 1}
                  </button>
                ))}
              </div>

              <div className="ads-panel-title" style={{ marginTop: 22 }}>Language</div>
              <select
                className="ads-panel-select"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                data-testid="ic-language"
              >
                <option value="en">English</option>
                <option value="sk">Slovenčina</option>
                <option value="cs">Čeština</option>
                <option value="de">Deutsch</option>
                <option value="pl">Polski</option>
                <option value="es">Español</option>
                <option value="fr">Français</option>
                <option value="hu">Magyar</option>
              </select>

              <button
                className="ads-btn ads-btn-primary"
                style={{ width: "100%", justifyContent: "center", marginTop: 22, padding: "14px 20px", fontSize: 16 }}
                onClick={run}
                disabled={loading}
                data-testid="ic-run"
              >
                {loading ? (<><Loader2 size={18} className="animate-spin" /> Generating 25 ads…</>) : (<><Sparkles size={18} /> Generate 25 ads now</>)}
              </button>
              <div style={{ fontSize: 11, color: "var(--ads-text-dim)", textAlign: "center", marginTop: 10 }}>
                Uses 25 ad credits. Drafts are saved to My Ads — edit or ship as-is.
              </div>
            </div>

            {/* Right: what you'll get */}
            <div className="ic-preview-card" data-testid="ic-preview-card">
              <div className="ads-panel-title">What you'll get</div>

              <div className="ic-matrix-title">5 angles</div>
              <div className="ic-list">
                {ANGLES.map((a) => (
                  <div key={a.id} className="ic-row">
                    <CheckCircle2 size={14} style={{ color: "var(--ads-accent)" }} />
                    <span className="ic-row-label">{a.label}</span>
                    <span className="ic-row-hint">{a.hint}</span>
                  </div>
                ))}
              </div>

              <div className="ic-matrix-title" style={{ marginTop: 20 }}>5 formats</div>
              <div className="ic-list">
                {FORMATS.map((f) => (
                  <div key={f.id} className="ic-row">
                    <CheckCircle2 size={14} style={{ color: "var(--ads-accent)" }} />
                    <span className="ic-row-label">{f.label}</span>
                    <span className="ic-row-hint">{f.size}</span>
                  </div>
                ))}
              </div>

              <div className="ic-total">
                <span>Total</span>
                <span className="ic-total-value">25 ads</span>
              </div>
            </div>
          </div>

          {result && (
            <div className="ic-success" data-testid="ic-success">
              <CheckCircle2 size={18} style={{ color: "#4ade80" }} />
              <span>
                Campaign <b>{result.campaign_id}</b> — {result.count} drafts ready.
                Redirecting to My Ads…
              </span>
            </div>
          )}
        </div>
      </div>
      <AchievementCelebration />
    </div>
  );
}
